import {useState} from 'react';
import {Document, Page, pdfjs} from 'react-pdf';
import {Box, Button, Typography} from '@mui/material';
import Loading from '../Loading';

import {LoadingWrapperStyles} from '~/modules/frontdesk/Finance/InvoiceDetails/styles';

// Setup PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface IPDFViewerProps {
  pdfData: string | undefined;
  previewOnly?: boolean;
  style?: React.CSSProperties;
  isLoading?: boolean;
  scale?: number;
}

const PDFViewer = ({
  isLoading,
  pdfData,
  style = {},
  previewOnly = true,
  scale = 1,
}: IPDFViewerProps) => {
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numPages, setNumPages] = useState<number>(0);

  const onDocumentLoadSuccess = ({numPages}: {numPages: number}) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const PDFLoadingError = () => {
    return (
      <Typography color="error">
        Ops! We have encountered an error while loading the pdf.
      </Typography>
    );
  };

  if (isLoading || !pdfData) {
    return (
      <Box sx={LoadingWrapperStyles}>
        <Loading color="info" />
      </Box>
    );
  }

  return (
    <>
      <Box sx={style} textAlign="center" className="shadow-md rounded-md">
        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          error={<PDFLoadingError />}
          loading={<Loading color="info" />}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderAnnotationLayer={false}
            renderTextLayer={false}
          />
        </Document>
      </Box>

      {!previewOnly && (
        <>
          <Typography className="mt-2">
            Page {pageNumber} of {numPages}
          </Typography>
          <Box className="mt-2 flex justify-center gap-2">
            <Button
              variant="outlined"
              onClick={goToPrevPage}
              disabled={pageNumber === 1}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              onClick={goToNextPage}
              disabled={pageNumber === numPages}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default PDFViewer;
